import { ChannelType } from 'api/ChannelsAPI';
import { MESSAGE_MEDIUMS } from 'constants/messageMediums';
import { SubscriptionPolicy } from 'modules/graphql/types';

export const NAV_LINKS = [
  {
    header: 'Manage',
    links: [
      {
        name: 'Email',
        pathname: '/settings/channels/email',
      },
      {
        name: 'In-app',
        pathname: '/settings/channels/inApp',
      },
      {
        name: 'Push',
        pathname: '/settings/channels/push',
      },
      {
        name: 'SMS',
        pathname: '/settings/channels/sms',
      },
      {
        name: 'Web push',
        pathname: '/settings/channels/webPush',
      },
    ],
  },
];

export const PAGE_TITLE = 'Subscription Channels';
export const DOCUMENT_TITLE = 'Subscription Channels';
export const SEARCH_DEBOUNCE_TIMEOUT_MS = 500;

const baseURL = 'https://support.iterable.com/hc/en-us/articles';
export const SUPPORT_LINKS = {
  [MESSAGE_MEDIUMS.EMAIL.dbKey]: `${baseURL}/7130133854612`,
  [MESSAGE_MEDIUMS.IN_APP.dbKey]: `${baseURL}/360035538391`,
  [MESSAGE_MEDIUMS.SMS.dbKey]: `${baseURL}/211970843`,
  [MESSAGE_MEDIUMS.PUSH.dbKey]: `${baseURL}/360035079872`,
  [MESSAGE_MEDIUMS.WEB_PUSH.dbKey]: `${baseURL}/4418166649748`,
};

export const CHANNEL_OVERFLOW_TEXT = {
  DELETE_LABEL: 'Delete subscription channel',
  DELETE_ID: 'delete-channel',
  UPDATE_LABEL: 'Edit subscription channel',
  UPDATE_ID: 'update-channel',
};

export const MESSAGE_TYPES_OVERFLOW_TEXT = {
  DELETE_ID: 'delete-message-type',
  DELETE_LABEL: 'Delete message type',
  FREQUENCY_CAP_ID: 'update-frequency-cap',
  FREQUENCY_CAP_LABEL: 'Update frequency cap',
  IGNORE_FREQUENCY_CAP_LABEL: 'Ignore frequency cap',
  IGNORE_FREQUENCY_CAP_ID: 'ignore-frequency-cap',
  RATE_LIMIT_ID: 'update-rate-limit',
  RATE_LIMIT_LABEL: 'Update rate limit',
  UPDATE_ID: 'update-message-type',
  UPDATE_LABEL: 'Edit message type',
  UPDATE_DOUBLE_OPT_IN_ID: 'update-to-double-opt-in-message-type',
  UPDATE_DOUBLE_OPT_IN_LABEL: 'Update to double opt-in',
};

/**
 * Form fields
 *
 */
// default or custom sending platforms
export const BINDING_TYPE = 'bindingType';
// custom sending platforms selected (bindingType === custom)
export const CHANNEL_BINDINGS = 'channelBindings';
// channel name
export const CHANNEL_NAME = 'channelName';
// marketing | transactional channel types
export const CHANNEL_TYPE = 'channelType';

export const CHANNEL_FORM_FIELDS_TEXT = {
  [CHANNEL_NAME]: {
    NAME: 'channelName',
    LABEL: 'Name',
    ID: 'channelName',
  },
  [CHANNEL_TYPE]: {
    NAME: 'channelType',
    LABEL: 'Type',
    ID: 'channelType',
  },
  [BINDING_TYPE]: {
    NAME: 'bindingType',
    LABEL: 'Sending platforms',
    ID: 'bindingType',
  },
};

export const CHANNEL_TYPE_OPTIONS = [
  {
    description:
      'Messages that promote your brand and have a marketing call-to-action, such as product sales. Each template must have an unsubscribe block with an unsubscribe link & your organization’s physical address, and has a sending limit of once per 18 hours.',
    id: 'marketing',
    name: 'Marketing',
    value: ChannelType.Marketing,
  },
  {
    description:
      'Messages related to conducting business, such as order confirmations. An unsubscribe block is not required, and there’s no limit to send frequency.',
    id: 'transactional',
    name: 'Transactional',
    value: ChannelType.Transactional,
  },
];
export const WHATS_APP_CHANNEL_TYPE_OPTIONS = [
  {
    description:
      'Messages that promote your brand and have a marketing call-to-action, such as product sales.',
    id: 'marketing',
    name: 'Marketing',
    value: ChannelType.Marketing,
  },
  {
    description: 'Messages related to conducting business, such as order confirmations.',
    id: 'transactional',
    name: 'Transactional',
    value: ChannelType.Transactional,
  },
];

export const MESSAGE_TYPE_FIELDS_TEXT = {
  RATE_LIMIT: {
    NAME: 'rateLimit',
    ID: 'rateLimit',
  },
  RATE_LIMIT_OPTION: {
    NAME: 'rateLimitOption',
    ID: 'rateLimitOption',
  },
  VALUES: {
    CUSTOM: 'custom',
    NONE: 'none',
  },
};

export const RATE_LIMIT_OPTIONS = [
  {
    description: 'Send campaigns as fast as possible',
    id: 'none',
    name: 'None',
    value: 'none',
  },
  {
    id: 'custom',
    name: 'Custom',
    value: 'custom',
  },
];

export const FREQUENCY_CAP_OPTIONS = [
  {
    id: 'none',
    description: 'Do not limit the number of campaigns sent from this message type',
    name: 'None',
    value: 'none',
  },
  {
    id: 'custom',
    name: 'Send a maximum of',
    value: 'custom',
  },
];

// set the max value for the rate limit input
export const maxIntValue = 2147483647;
export const ID_PREFIX = 'channel-';

export const ERROR_TEXT = {
  NO_NAME: 'Enter a name',
  EXCEEDS_PERCENTAGE: 'Exceeds 100%',
  INVALID_NUMBER: 'Numbers should be between 0 and 100',
  UNSELECTED_ACCOUNT: 'Select a connected account to split sends or clear the row.',
  DUPLICATE_ACCOUNTS: 'Sending platforms can only be selected once.',
};

export const SUBSCRIPTION_POLICY: { [key: string]: string } = {
  [SubscriptionPolicy.OptIn]: 'Opt-in',
  [SubscriptionPolicy.OptOut]: 'Opt-out',
  [SubscriptionPolicy.DoubleOptIn]: 'Double opt-in',
};

export const SCHEMA_ERROR_TEXT = {
  minValue: 'Number must be greater than zero',
  maxValue: 'Max value exceeded',
  regex: 'Must enter a number',
};

export const DEFAULT_MESSAGE_TYPE_METADATA = 'Do not cap sends';
